import { useEffect, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { requestAll } from '../../../apis/config';
import { fileManagerApis } from '../../../apis/fileManagerApis';
import { serverApis } from '../../../apis/serverApis';
import Loading from '../../../components/elements/loading/Loading';
import Editor from '../../../components/page-sections/editor/Editor';
import Breadcrumbs from '../../../components/sections/breadcrumbs/Breadcrumbs';
import TitlePanel from '../../../components/sections/title-panel/TitlePanel';
import { handleErrors } from '../../../helpers';
import useToast, { TOAST_TYPE } from '../../../hooks/useToast';
import { PRIVATE_ROUTE } from '../../../routes/routes';
import { getFileFormData, sentenceCase } from '../../../utils/utils';

const FileEditorPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId, gpsId } = useParams();
  const { showToast } = useToast();
  const { t } = useTranslation();

  const [server, setServer] = useState(null);
  const [code, setCode] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const state = location.state;
  const path = state.folderChain.reduce(
    (prev, curr) => `${prev}/${curr.name}`,
    []
  );

  const isXml =
    state?.file?.ext === 'xml' || state?.file?.name === 'managed-schema';
  const mode = isXml ? 'xml' : state?.file?.ext;

  useEffect(() => {
    if (isEmpty(state)) {
      // Since the state is empty, there are two possibilities:
      // If the user tries to access the page directly, redirect to the projects page, otherwise go back
      const isHistoryStackEmpty = location.key === 'default';
      isHistoryStackEmpty ? navigate(PRIVATE_ROUTE.PROJECTS) : navigate(-1);
      return;
    }

    const fetchFile = async () => {
      const {
        file: { name },
        subscriptNum,
      } = state;

      try {
        const [serverRes, fileRes] = await requestAll([
          serverApis.getServer(projectId, gpsId),
          fileManagerApis.downloadFiles(
            projectId,
            gpsId,
            false,
            subscriptNum,
            path,
            name
          ),
        ]);

        setServer(serverRes.data);
        if (typeof fileRes.data !== 'string' && mode !== 'json') {
          setCode(JSON.stringify(fileRes.data, null, 4));
        } else {
          setCode(fileRes.data);
        }
      } catch (error) {
        handleErrors(navigate, error, showToast);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFile();
  }, [projectId, gpsId]);

  const onSubmit = async (mode, code) => {
    const getFile = (mode) => {
      switch (mode) {
        case 'xml':
          return new File([code], state.file.name, {
            type: 'text/xml',
          });
        case 'shell':
          return new File([code], state.file.name, {
            type: 'application/x-sh',
          });
        default:
          return new File([code], state.file.name, {
            type: 'text/plain;charset=utf-8',
          });
      }
    };

    try {
      const { subscript } = state;
      const formData = getFileFormData('files', getFile(mode));
      await fileManagerApis.uploadFiles(
        projectId,
        gpsId,
        subscript,
        path,
        formData
      );
      showToast(
        TOAST_TYPE.SUCCESS,
        sentenceCase(
          t('updateSuccess', {
            ns: 'notifications',
            text: t('file', { ns: 'fields' }),
          })
        )
      );
      setTimeout(
        () =>
          navigate(state.previousLocation, {
            state: { ...state },
          }),
        800
      );
    } catch (error) {
      showToast(TOAST_TYPE.ERROR, t('updateFile', { ns: 'errors' }));
      setIsLoading(false);
    }
  };

  const editorContent =
    code !== null ? (
      <>
        <Breadcrumbs serverName={server?.name} />
        <TitlePanel
          title={`${t('fileEditor', { ns: 'titles' })} - ${state.file.name}`}
        />
        <Editor mode={mode} code={code} onSubmit={onSubmit} />
      </>
    ) : (
      <Box sx={{ mt: 2 }}>
        <Alert severity="error">
          {sentenceCase(
            t('loading', {
              ns: 'errors',
              content: t('requestedFile', { ns: 'instance' }),
            })
          )}
        </Alert>
      </Box>
    );

  return (
    <Container>
      {isLoading && <Loading />}
      {!isLoading && editorContent}
    </Container>
  );
};

export default FileEditorPage;
